// Iter73 — Scanner Result Normalization helpers.
//
// Shared, display-only React helpers that normalize the visual
// presentation of scanner results across every module panel
// (BPCS, BCCS, Iron Condor, CFM-CC-SHARES, CFM-CC-LEAPS) and the
// protected Wheel ITM passthrough.
//
// HARD CONSTRAINTS:
//  * Pure render. Zero network activity. No axios / fetch / XHR /
//    beacon. No effect-hook side effects.
//  * No computation that would shadow or duplicate evaluator logic
//    — the helpers ONLY read fields that the backend evaluator
//    already produced and render them as chips.
//  * No recommendation, no ranking, no "winner" selection.

import React from "react";
import { AlertTriangle } from "lucide-react";

// ── Canonical status colour map ───────────────────────────────────────
//
// Every scanner result `status` value the evaluators emit maps to a
// single, consistent visual treatment. Panels are encouraged to
// import this map rather than redefining colours per-panel.
//
// Categories:
//   * SUCCESS (green)         : ENTER
//   * NEUTRAL / WAIT (amber)  : WATCH, BUYBACK, ROLL_OUT_UP,
//                                CLOSE_DIAGONAL, ALLOW_ASSIGNMENT
//   * URGENT / DEFENSIVE (red): ASSIGNMENT_RISK, DEFEND, DEFEND_LEAPS,
//                                BLOCKED, ERROR
//   * DORMANT (grey)          : NO_TRADE

export const STATUS_COLOR_MAP = Object.freeze({
  // success
  ENTER:             "border-[#00FF88]/40 bg-[#00FF88]/10 text-[#00FF88]",
  // neutral / wait
  WATCH:             "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  BUYBACK:           "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  ROLL_OUT_UP:       "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  CLOSE_DIAGONAL:    "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  ALLOW_ASSIGNMENT:  "border-[#FFB800]/40 bg-[#FFB800]/10 text-[#FFB800]",
  // urgent / defensive
  ASSIGNMENT_RISK:   "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  DEFEND:            "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  DEFEND_LEAPS:      "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  BLOCKED:           "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  ERROR:             "border-[#FF3366]/40 bg-[#FF3366]/10 text-[#FF3366]",
  // dormant
  NO_TRADE:          "border-[#5F5F66]/40 bg-[#121216] text-[#8A8A93]",
});

export const STATUS_NEUTRAL_DEFAULT = STATUS_COLOR_MAP.NO_TRADE;

// ── Metric chip ───────────────────────────────────────────────────────

function fmtNumber(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  // Compact display: 2 decimals for fractions, otherwise integer.
  if (Math.abs(n - Math.round(n)) < 1e-9) return String(Math.round(n));
  return n.toFixed(2);
}

export function MetricChip({ label, value, testid, accent }) {
  const text = fmtNumber(value);
  if (text === null) return null;
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[10px] font-mono text-[#E0E0E0]"
    >
      <span className={`uppercase tracking-[0.12em] ${accent || "text-[#5F5F66]"}`}>
        {label}
      </span>
      <span className="font-bold">{text}</span>
    </span>
  );
}

// ── ReasonCodeChips — shared reason-code chip row ────────────────────

export function ReasonCodeChips({ codes, testid }) {
  if (!Array.isArray(codes) || codes.length === 0) return null;
  return (
    <span
      data-testid={testid}
      className="flex flex-wrap gap-1 ml-1"
    >
      {codes.map((rc, i) => (
        <span
          key={`${rc}-${i}`}
          className="px-1.5 py-0.5 rounded-sm border border-[#26262B] bg-[#121216] text-[9px] text-[#E0E0E0]"
        >
          {rc}
        </span>
      ))}
    </span>
  );
}

// ── ScannerWarnings — list of warning rows ───────────────────────────

export function ScannerWarnings({ warnings, testid }) {
  if (!Array.isArray(warnings) || warnings.length === 0) return null;
  return (
    <div
      data-testid={testid}
      className="mt-1 text-[10px] font-mono text-[#FFB800]"
    >
      {warnings.map((w, i) => (
        <div key={i} className="flex items-start gap-1">
          <AlertTriangle size={10} className="mt-0.5 shrink-0" />
          {w}
        </div>
      ))}
    </div>
  );
}

// ── ScannerErrorMessage — uniform error block ────────────────────────

export function ScannerErrorMessage({ message, testid }) {
  if (!message) return null;
  return (
    <div
      data-testid={testid}
      className="mt-1 text-[10px] font-mono text-[#FF3366] flex items-start gap-1"
    >
      <AlertTriangle size={10} className="mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

// ── ScannerSpreadMetrics — per-module metric chips ───────────────────
//
// Reads ONLY fields the evaluator already produced. The component
// never computes new financial values; it surfaces what the
// scanner returned.

function getStruct(rec) {
  // Each evaluator places its structure under one of two keys.
  return rec?.selected_structure || rec?.option_plan ||
          rec?.selected_candidate || null;
}

export function ScannerSpreadMetrics({ rec, module: mod, testidPrefix }) {
  const s = getStruct(rec);
  if (!s) return null;
  const symbol = rec?.symbol || "";
  const tid = (suffix) => `${testidPrefix}-${suffix}-${symbol}`;

  if (mod === "BPCS" || mod === "BCCS") {
    return (
      <div
        data-testid={tid("metrics")}
        className="mt-1 flex flex-wrap gap-x-3 gap-y-1"
      >
        <MetricChip label="Short" value={s.short_strike}
                     testid={tid("short-strike")} />
        <MetricChip label="Long"  value={s.long_strike}
                     testid={tid("long-strike")} />
        <MetricChip label="Credit" value={s.credit ?? s.net_credit ?? s.total_credit}
                     testid={tid("credit")} />
        <MetricChip label="Max profit" value={s.max_profit}
                     testid={tid("max-profit")} />
        <MetricChip label="Max loss"   value={s.max_loss}
                     testid={tid("max-loss")} />
        <MetricChip label="Breakeven"  value={s.breakeven ?? s.break_even}
                     testid={tid("breakeven")} />
        <MetricChip label="ROR"        value={s.return_on_risk ?? s.roi}
                     testid={tid("ror")} />
      </div>
    );
  }
  if (mod === "IRON_CONDOR") {
    // Iron condor evaluator may surface put + call sub-structures
    // or individual strike fields. Read both shapes defensively.
    const ps = s.put_spread || {};
    const cs = s.call_spread || {};
    return (
      <div
        data-testid={tid("metrics")}
        className="mt-1 flex flex-wrap gap-x-3 gap-y-1"
      >
        <MetricChip label="Put short"
                     value={ps.short_strike ?? s.put_short_strike}
                     testid={tid("put-short")} />
        <MetricChip label="Put long"
                     value={ps.long_strike ?? s.put_long_strike}
                     testid={tid("put-long")} />
        <MetricChip label="Call short"
                     value={cs.short_strike ?? s.call_short_strike}
                     testid={tid("call-short")} />
        <MetricChip label="Call long"
                     value={cs.long_strike ?? s.call_long_strike}
                     testid={tid("call-long")} />
        <MetricChip label="Total credit"
                     value={s.total_credit ?? s.credit}
                     testid={tid("total-credit")} />
        <MetricChip label="Max profit" value={s.max_profit}
                     testid={tid("max-profit")} />
        <MetricChip label="Max loss"   value={s.max_loss}
                     testid={tid("max-loss")} />
        <MetricChip label="Lower BE"
                     value={s.lower_breakeven ?? s.breakeven_low}
                     testid={tid("lower-be")} />
        <MetricChip label="Upper BE"
                     value={s.upper_breakeven ?? s.breakeven_high}
                     testid={tid("upper-be")} />
        <MetricChip label="Range width"
                     value={s.range_width ?? s.compression_pct}
                     testid={tid("range-width")} />
      </div>
    );
  }
  return null;
}
